/**
 * Decides how CRM role changes are mirrored into the user's Hub `Roles` array.
 *
 * Only "CRM Admin" is mirrored: many frontend gates and several backend
 * callables authorize via Roles.includes('CRM Admin'), so granting/revoking
 * CRM Admin must keep the array in sync.
 *
 * "Loan Officer" is deliberately NEVER mirrored. The Hub "Loan Officer" role
 * changes which loans the legacy Submissions pages show (LO-scoped instead of
 * CSC/TC-scoped), so writing it as a side effect of CRM access polluted ops
 * staff accounts (Aug 2026: five CSCs lost their CSC loan views this way).
 * Genuine LOs already carry the role from their Google Workspace org title,
 * synced at registration; CRM access must not grant or revoke it.
 */

export const HUB_MIRRORED_CRM_ROLES = ["CRM Admin"];

export function shouldMirrorCrmRole(role) {
  return HUB_MIRRORED_CRM_ROLES.includes(role);
}

/**
 * Given a CRM role transition, returns which Hub role (if any) to add to and
 * remove from the `Roles` array.
 *
 * Covers all three admin flows:
 *   - grant default:   getHubRolesMirror(null, 'Loan Officer')
 *   - dropdown change: getHubRolesMirror(oldRole, newRole)
 *   - revoke:          getHubRolesMirror(user.CRMRole, null)
 *
 * @param {string|null|undefined} oldRole - the user's current CRMRole
 * @param {string|null|undefined} newRole - the CRMRole being set (null on revoke)
 * @returns {{ addToRoles: string|null, removeFromRoles: string|null }}
 */
export function getHubRolesMirror(oldRole, newRole) {
  const removeFromRoles =
    oldRole && oldRole !== newRole && shouldMirrorCrmRole(oldRole) ? oldRole : null;
  const addToRoles =
    newRole && newRole !== oldRole && shouldMirrorCrmRole(newRole) ? newRole : null;
  return { addToRoles, removeFromRoles };
}
